import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useUserDetails } from '@/features/users/api';
import { api } from '@/lib/api/client';
import { useQuery } from '@tanstack/react-query';

interface ActivityLogRow {
  id: number;
  action?: string;
  userId: number;
  username?: string;
  createdAt?: string;
  requestIp?: string;
}

interface ActivityLogUser {
  userId: number;
  username: string;
}

const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ACTION_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  LOGIN: 'default',
  LOGOUT: 'secondary',
  SWITCH_LOGIN: 'outline',
};

export function ActivityLogsPage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const profile = useUserDetails(Number(userId));
  const userType = profile.data?.userType ?? '';

  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [searchKey, setSearchKey] = useState(0); // triggers refetch

  const isSuperAdmin = (userType as string) === 'superadmin';
  const isUser = role === 'ROLE_USER';

  /* ---- Fetch user list for filter dropdown ---- */
  const usersQuery = useQuery({
    queryKey: ['activity-logs', 'users', userId, userType],
    queryFn: () =>
      api.get<ActivityLogUser[]>(
        `/api/obd/activity/logs/users?userId=${userId}&userType=${userType}`,
      ),
    enabled: Boolean(userId) && Boolean(userType) && !isUser,
    staleTime: 5 * 60_000,
    select: (data) => (Array.isArray(data) ? data : []),
  });

  /* ---- Fetch logs ---- */
  const logsQuery = useQuery({
    queryKey: ['activity-logs', 'list', userId, userType, fmt(startDate), fmt(endDate), filterUserId, searchKey],
    queryFn: async () => {
      let url = `/api/obd/activity/logs?userId=${userId}&userType=${userType}&startDate=${fmt(startDate)}&endDate=${fmt(endDate)}`;
      if (filterUserId) url += `&filterUserId=${filterUserId}`;
      const res = await api.get<ActivityLogRow[]>(url);
      return Array.isArray(res) ? res : [];
    },
    enabled: Boolean(userId) && Boolean(userType),
    staleTime: 30_000,
  });

  const logs = logsQuery.data ?? [];
  const users = usersQuery.data ?? [];

  if (role !== USER_ROLES.S_ADMIN) return <Navigate to="/" replace />;

  /* ---- Columns ---- */
  const columns = useMemo<ColumnDef<ActivityLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '#',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 140,
        cell: ({ row }) => {
          const action = row.original.action ?? '';
          const variant = ACTION_VARIANT[action] ?? 'outline';
          return (
            <Badge variant={variant} className="text-xs">
              {action}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'username',
        header: 'Username',
      },
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'requestIp',
        header: 'IP Address',
        size: 150,
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.requestIp ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Date & Time',
        size: 190,
      },
    ],
    [],
  );

  /* ---- Handlers ---- */
  const handleSearch = () => setSearchKey((k) => k + 1);

  const handleReset = () => {
    const t = new Date();
    setStartDate(t);
    setEndDate(t);
    setFilterUserId('');
    setSearchKey((k) => k + 1);
  };

  if (role !== USER_ROLES.S_ADMIN) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Activity Logs"
        description={isSuperAdmin ? 'Login and logout activity across all users.' : 'Login and logout activity for your account.'}
      />

      {/* Filter */}
      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>Narrow logs by date range and user.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">Start date</label>
              <DatePicker
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                toDate={new Date()}
              />
            </div>
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium">End date</label>
              <DatePicker
                value={endDate}
                onChange={(d) => d && setEndDate(d)}
                fromDate={startDate}
                toDate={new Date()}
              />
            </div>

            {!isUser && (
              <div className="min-w-[200px] flex-1">
                <label className="mb-1 block text-sm font-medium">User</label>
                <Select value={filterUserId} onValueChange={setFilterUserId}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={usersQuery.isLoading ? 'Loading…' : '-- All Users --'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">-- All Users --</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.userId} value={String(u.userId)}>
                        {u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button onClick={handleSearch} disabled={logsQuery.isFetching}>
              {logsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={logsQuery.isFetching}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable<ActivityLogRow, unknown>
        tableId="activity-logs:v1"
        columns={columns}
        data={logs}
        isLoading={logsQuery.isFetching}
        searchPlaceholder="Search by username, IP, action..."
        emptyTitle="No activity logs"
        emptyDescription="No logs found for the selected filters."
        getRowId={(row) => String(row.id)}
        exportFilename={`activity-logs-${fmt(new Date())}.csv`}
        pageSize={25}
      />
    </FillPage>
  );
}